import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["folderList", "folderPath"]
  
  connect() {
    // Check if we're connected to Dropbox
    if (document.querySelector('[data-dropbox-connected="true"]')) {
      // Add event listener to the browse button
      const browseButton = document.getElementById('browse-folder-btn')
      if (browseButton) {
        browseButton.addEventListener('click', this.loadFolders.bind(this))
      }
      
      // Add event listener for folder selection
      this.element.addEventListener('click', this.handleFolderClick.bind(this))
      
      // Output folder now always visible
      
      // Add event listener for "Browse Output Folder" button if it exists
      const browseOutputButton = document.getElementById('browse-output-folder-btn')
      if (browseOutputButton) {
        browseOutputButton.addEventListener('click', () => {
          // Set a flag to indicate we're browsing for the output folder
          this.element.dataset.browsingFor = 'output'
          this.loadFolders()
          // Update the UI to indicate we're selecting the output folder
          const notification = document.createElement('div')
          notification.className = 'alert alert-info mt-3'
          notification.innerHTML = '<i class="fa-solid fa-info-circle me-2"></i> Select a folder for processed documents'
          this.folderListTarget.prepend(notification)
        })
      }
      
      // Add event listener for the main browse button
      const browseFolderButton = document.getElementById('browse-folder-btn')
      if (browseFolderButton) {
        browseFolderButton.addEventListener('click', () => {
          // Set a flag to indicate we're browsing for the inbox folder
          this.element.dataset.browsingFor = 'inbox'
          this.loadFolders()
        })
      }
    }
  }
  
  // Output folder is now always visible
  
  async loadFolders(event) {
    // If clicked with a specific path, use it; otherwise use empty string to get root
    const path = event && event.target && event.target.dataset.path ? event.target.dataset.path : ""
    
    try {
      // Show loading indicator
      this.folderListTarget.innerHTML = '<div class="d-flex align-items-center mb-3"><div class="spinner-border spinner-border-sm me-2" role="status"></div> Loading folders...</div>'
      
      // Fetch folders from server using the routes path provided by data-list-folders-url attribute
      const listFoldersUrl = this.element.dataset.listFoldersUrl || "/dashboard/automation/list_dropbox_folders"
      const response = await fetch(`${listFoldersUrl}?path=${encodeURIComponent(path)}`)
      const data = await response.json()
      
      if (data.error) {
        this.folderListTarget.innerHTML = `<div class="alert alert-danger">${data.error}</div>`
        return
      }
      
      if (!data.folders || data.folders.length === 0) {
        // Check if we're browsing for output or inbox
        const browsingFor = this.element.dataset.browsingFor || 'inbox'
        const folderTypeText = browsingFor === 'output' ? 'output' : 'inbox'
        
        this.folderListTarget.innerHTML = `
          <div class="alert alert-light">
            <p class="mb-0">No subfolders found in ${path || 'root directory'}</p>
            <div class="dropbox-folder-actions mt-3">
              <button type="button" class="btn btn-sm btn-primary select-current-folder" data-path="${path}">
                Select as ${folderTypeText} folder
              </button>
            </div>
          </div>`
        return
      }
      
      // Render the folder hierarchy
      let html = `
        <div class="dropbox-folder-browser mb-3">
          <div class="dropbox-folder-path">
            <ol class="breadcrumb mb-3">
              <li class="breadcrumb-item">
                <a href="#" data-path="" class="folder-breadcrumb">Root</a>
              </li>
      `
      
      // Create breadcrumbs
      if (path) {
        const pathParts = path.split('/').filter(Boolean)
        let currentPath = ""
        
        pathParts.forEach((part, index) => {
          currentPath += `/${part}`
          const isLast = index === pathParts.length - 1
          
          html += `
            <li class="breadcrumb-item ${isLast ? 'active' : ''}">
              ${isLast ? part : `<a href="#" data-path="${currentPath}" class="folder-breadcrumb">${part}</a>`}
            </li>
          `
        })
      }
      
      html += `
          </ol>
        </div>
        <div class="dropbox-folder-list-container">
          <div class="row g-2">
      `
      
      // Add parent folder option if not at root
      if (path) {
        const parentPath = path.split('/').slice(0, -1).join('/')
        html += `
          <div class="col-md-4 col-sm-6">
            <div class="dropbox-folder-item" data-path="${parentPath}">
              <i class="fa-solid fa-folder-arrow-up me-2 text-warning"></i>
              <span>Parent directory</span>
            </div>
          </div>
        `
      }
      
      // Add folders
      data.folders.forEach(folder => {
        html += `
          <div class="col-md-4 col-sm-6">
            <div class="dropbox-folder-item" data-path="${folder.path}">
              <i class="fa-solid fa-folder me-2 text-warning"></i>
              <span>${folder.name}</span>
            </div>
          </div>
        `
      })
      
      // Check if we're browsing for output or inbox
      const browsingFor = this.element.dataset.browsingFor || 'inbox'
      const folderTypeText = browsingFor === 'output' ? 'output' : 'inbox'
      
      html += `
          </div>
        </div>
        <div class="dropbox-folder-actions mt-3">
          <button type="button" class="btn btn-sm btn-primary select-current-folder" data-path="${path}">
            Select as ${folderTypeText} folder
          </button>
        </div>
      `
      
      this.folderListTarget.innerHTML = html
    } catch (error) {
      console.error('Error fetching folders:', error)
      this.folderListTarget.innerHTML = `<div class="alert alert-danger">Error loading folders: ${error.message}</div>`
    }
  }
  
  handleFolderClick(event) {
    const element = event.target.closest('.dropbox-folder-item, .folder-breadcrumb, .select-current-folder')
    if (!element) return
    
    // Prevent default link behavior
    event.preventDefault()
    
    // Get the path
    const path = element.dataset.path
    
    // Check if this is the "Select Current Directory" button
    if (element.classList.contains('select-current-folder')) {
      this.selectFolder(path)
      return
    }
    
    // If it's a folder or breadcrumb, load the subfolder
    this.loadFolders({ target: { dataset: { path } } })
  }
  
  selectFolder(path) {
    // Check if we're selecting for output folder or inbox folder based on the dataset flag
    const browsingFor = this.element.dataset.browsingFor || 'inbox'
    const isSelectingOutputFolder = browsingFor === 'output'
    
    // Determine which input to update based on what we're browsing for
    let targetInput
    if (isSelectingOutputFolder) {
      targetInput = document.getElementById('output-folder-path')
    } else {
      targetInput = this.hasFolderPathTarget ? this.folderPathTarget : null
    }
    
    // Set the path in the appropriate input field
    if (targetInput) {
      targetInput.value = path || '/'
    }
    
    // Highlight the selected folder
    const notification = document.createElement('div')
    notification.className = 'alert alert-success mt-3 mb-0'
    notification.innerHTML = `<i class="fa-solid fa-check-circle me-2"></i> Selected ${isSelectingOutputFolder ? 'output' : 'inbox'} folder: <strong>${path || 'Root directory'}</strong>`
    
    // Remove any existing notifications
    const existingNotifications = this.folderListTarget.querySelectorAll('.alert')
    existingNotifications.forEach(note => note.remove())
    
    // Add the notification after the folder actions
    const folderActions = this.folderListTarget.querySelector('.dropbox-folder-actions')
    if (folderActions) {
      folderActions.after(notification)
    } else {
      this.folderListTarget.appendChild(notification)
    }
    
    // If this was for output folder selection, add a "Switch to inbox folder" button 
    // after the success message
    if (isSelectingOutputFolder) {
      setTimeout(() => {
        const switchButton = document.createElement('div')
        switchButton.className = 'mt-3 text-center'
        switchButton.innerHTML = `
          <button type="button" class="btn btn-outline-primary btn-sm" id="switch-to-inbox">
            <i class="fa-solid fa-arrow-left me-1"></i> Browse for inbox folder
          </button>
        `
        notification.after(switchButton)
        
        // Add event listener to the switch button
        document.getElementById('switch-to-inbox').addEventListener('click', () => {
          this.element.dataset.browsingFor = 'inbox'
          this.loadFolders() // Reset to inbox folder browsing
        })
      }, 500)
    } else if (document.getElementById('output-folder-path')) {
      // If we were selecting the inbox folder, add a "Switch to output folder" button
      setTimeout(() => {
        const switchButton = document.createElement('div')
        switchButton.className = 'mt-3 text-center'
        switchButton.innerHTML = `
          <button type="button" class="btn btn-outline-primary btn-sm" id="switch-to-output">
            <i class="fa-solid fa-arrow-right me-1"></i> Browse for output folder
          </button>
        `
        notification.after(switchButton)
        
        // Add event listener to the switch button
        document.getElementById('switch-to-output').addEventListener('click', () => {
          this.element.dataset.browsingFor = 'output'
          this.loadFolders() // Reset to output folder browsing
          
          // Add an info banner
          const infoBanner = document.createElement('div')
          infoBanner.className = 'alert alert-info mt-3'
          infoBanner.innerHTML = '<i class="fa-solid fa-info-circle me-2"></i> Select a folder for processed documents'
          this.folderListTarget.prepend(infoBanner)
        })
      }, 500)
    }
  }
}